import { useTranslation } from 'react-i18next';
import type { RunOutcome } from '@complan/contracts';
import { cn } from '@/lib/utils';

/**
 * The Administration screen's status-style tags, following UI spec §3.4: a pill,
 * radius 3px, 10.5px, 600, tinted background with darker text.
 *
 * These reuse the same tinted-tag vocabulary the calendar and intake screens use
 * for status, priority, confidential and capacity-state tags — here applied to
 * the active/inactive state of a master data record and the succeeded/partial/
 * failed outcome of a backup run or restore verification.
 */

const TAG_BASE =
  'inline-flex w-fit items-center rounded-[3px] px-1.5 py-px text-[10.5px] font-semibold leading-[17px]';

/** The active/inactive state of a master data record (R16.5). */
export function StateBadge({ isActive }: { readonly isActive: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        TAG_BASE,
        isActive
          ? 'bg-[#EAF5EF] text-[#14724A]'
          : // `--grey` (#8794A5) on `--line-soft` is only 2.7:1 — below the 4.5:1
            // R18.7/R18.15 floor, and the inactive state is exactly the one a
            // reader needs to be able to spot. `--label` on the same tint is
            // ~9:1; the state stays distinguishable from Active by its tint and
            // its word, so nothing is lost by drawing it legibly.
            'bg-[var(--line-soft)] text-[var(--label)]',
      )}
    >
      {isActive ? t('admin.common.active') : t('admin.common.inactive')}
    </span>
  );
}

/** A yes/no flag rendered as an accent tag when set, muted when unset. */
export function FlagBadge({
  on,
  label,
}: {
  readonly on: boolean;
  readonly label: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        TAG_BASE,
        on ? 'bg-[var(--accent-soft)] text-[var(--accent-hover)]' : 'bg-transparent text-muted-foreground',
      )}
    >
      {label}
    </span>
  );
}

/** The completion outcome of a backup run or a restore verification (R17.6, R17.10). */
export function OutcomeBadge({ outcome }: { readonly outcome: RunOutcome }): React.JSX.Element {
  const { t } = useTranslation();
  const tone =
    outcome === 'succeeded'
      ? 'bg-[#EAF5EF] text-[#14724A]'
      : outcome === 'partial'
        ? 'bg-[#FFF8E8] text-[#7D5300]'
        : 'bg-[#FCEDEB] text-[#8E2A1E]';
  return <span className={cn(TAG_BASE, tone)}>{t(`admin.operations.outcome.${outcome}`)}</span>;
}
